import Phaser from 'phaser';

export type BgmKey =
  | 'bgm_lorencia'
  | 'bgm_noria'
  | 'bgm_devias'
  | 'bgm_devias_blizzard'
  | 'bgm_dungeon_calm'
  | 'bgm_dungeon_intense'
  | 'bgm_atlans'
  | 'bgm_tarkan'
  | 'bgm_icarus'
  | 'bgm_kalima_1'
  | 'bgm_boss_kundun'
  | 'bgm_blood_castle'
  | 'bgm_devil_square'
  | 'bgm_chaos_castle'
  | 'bgm_illusion_temple'
  | 'bgm_crywolf_battle'
  | 'bgm_crywolf_boss'
  | 'bgm_castle_siege_intro'
  | 'bgm_castle_siege_main'
  | 'bgm_castle_siege_climax'
  | 'bgm_golden_alert'
  | 'bgm_hero_dungeon_calm'
  | 'bgm_boss_guardian';

export const BGM_FILES: Record<BgmKey, string> = {
  bgm_lorencia: 'bgm_lorencia.mp3',
  bgm_noria: 'bgm_noria.mp3',
  bgm_devias: 'bgm_devias.ogg',
  bgm_devias_blizzard: 'bgm_devias_blizzard.ogg',
  bgm_dungeon_calm: 'bgm_dungeon_calm.ogg',
  bgm_dungeon_intense: 'bgm_dungeon_intense.mp3',
  bgm_atlans: 'bgm_atlans.ogg',
  bgm_tarkan: 'bgm_tarkan.mp3',
  bgm_icarus: 'bgm_icarus.wav',
  bgm_kalima_1: 'bgm_kalima_1.ogg',
  bgm_boss_kundun: 'bgm_boss_kundun.mp3',
  bgm_blood_castle: 'bgm_blood_castle.ogg',
  bgm_devil_square: 'bgm_devil_square.mp3',
  bgm_chaos_castle: 'bgm_chaos_castle.mp3',
  bgm_illusion_temple: 'bgm_illusion_temple.mp3',
  bgm_crywolf_battle: 'bgm_crywolf_battle.mp3',
  bgm_crywolf_boss: 'bgm_crywolf_boss.mp3',
  bgm_castle_siege_intro: 'bgm_castle_siege_intro.wav',
  bgm_castle_siege_main: 'bgm_castle_siege_main.wav',
  bgm_castle_siege_climax: 'bgm_castle_siege_climax.wav',
  bgm_golden_alert: 'bgm_golden_alert.ogg',
  bgm_hero_dungeon_calm: 'bgm_hero_dungeon_calm.mp3',
  bgm_boss_guardian: 'bgm_boss_guardian.wav',
};

export const BGM_BY_MAP_ID: Record<number, BgmKey> = {
  1: 'bgm_lorencia',
  2: 'bgm_noria',
  3: 'bgm_devias',
  4: 'bgm_dungeon_calm',
  5: 'bgm_atlans',
  6: 'bgm_tarkan',
  7: 'bgm_icarus',
  8: 'bgm_kalima_1',
  9: 'bgm_blood_castle',
  10: 'bgm_devil_square',
  11: 'bgm_chaos_castle',
  12: 'bgm_illusion_temple',
  13: 'bgm_crywolf_battle',
  14: 'bgm_castle_siege_main',
  15: 'bgm_golden_alert',
  16: 'bgm_hero_dungeon_calm',
  100: 'bgm_lorencia',
};

// Chỉ preload các BGM map nhẹ + thường gặp (MVP). Còn lại lazy-load on demand.
const PRELOAD_KEYS: BgmKey[] = [
  'bgm_lorencia',
  'bgm_noria',
  'bgm_devias',
  'bgm_dungeon_calm',
  'bgm_tarkan',
];

const FADE_MS = 600;
const STORAGE_KEY = 'hw_bgm_volume';
const MUTE_KEY = 'hw_bgm_muted';
const BASE_PATH = '/assets/downloads/bgm';

class AudioManagerImpl {
  private current?: Phaser.Sound.BaseSound;
  private currentKey?: BgmKey;
  private volume = 0.5;
  private muted = false;
  private fadeTimer?: number;

  init() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) this.volume = Math.max(0, Math.min(1, parseFloat(stored)));
    this.muted = localStorage.getItem(MUTE_KEY) === '1';
  }

  /** Preload only the small/common BGM (MVP maps) to avoid decode jank. */
  preloadAll(scene: Phaser.Scene) {
    for (const key of PRELOAD_KEYS) {
      if (!scene.cache.audio.exists(key)) {
        scene.load.audio(key, `${BASE_PATH}/${BGM_FILES[key]}`);
      }
    }
  }

  /** Play BGM. If not in cache, lazy-load then play. */
  play(scene: Phaser.Scene, key: BgmKey) {
    if (this.currentKey === key && this.current?.isPlaying) return;
    if (scene.cache.audio.exists(key)) {
      this.startTrack(scene, key);
      return;
    }
    const url = `${BASE_PATH}/${BGM_FILES[key]}`;
    scene.load.audio(key, url);
    scene.load.once(`filecomplete-audio-${key}`, () => {
      this.startTrack(scene, key);
    });
    scene.load.start();
  }

  stop() {
    this.fadeOutCurrent();
    this.current = undefined;
    this.currentKey = undefined;
  }

  playForMap(scene: Phaser.Scene, mapId: number) {
    const key = BGM_BY_MAP_ID[mapId];
    if (key) this.play(scene, key);
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    localStorage.setItem(STORAGE_KEY, String(this.volume));
    if (this.current && !this.muted) {
      this.applyVolume(this.current, this.volume);
    }
  }

  getVolume() { return this.volume; }

  setMuted(m: boolean) {
    this.muted = m;
    localStorage.setItem(MUTE_KEY, m ? '1' : '0');
    if (this.current) {
      this.applyVolume(this.current, m ? 0 : this.volume);
    }
  }

  isMuted() { return this.muted; }

  // --- internals ---

  private startTrack(scene: Phaser.Scene, key: BgmKey) {
    const target = scene.sound.add(key, { loop: true, volume: 0 });
    target.play();
    this.fadeOutCurrent();
    this.current = target;
    this.currentKey = key;
    this.fadeIn(target, this.muted ? 0 : this.volume);
  }

  /**
   * Lightweight fade using a single requestAnimationFrame loop instead of
   * Phaser tween (which iterates per-frame on the sound object's volume
   * property and was a significant FPS hit during BGM crossfades).
   */
  private fadeIn(sound: Phaser.Sound.BaseSound, targetVol: number) {
    const start = performance.now();
    const from = 0;
    const to = targetVol;
    const step = () => {
      if (!sound.isPlaying) return;
      const t = Math.min(1, (performance.now() - start) / FADE_MS);
      this.applyVolume(sound, from + (to - from) * t);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  private fadeOutCurrent() {
    const cur = this.current;
    if (!cur) return;
    if (this.fadeTimer !== undefined) {
      cancelAnimationFrame(this.fadeTimer);
      this.fadeTimer = undefined;
    }
    if (!cur.isPlaying) {
      try { cur.stop(); cur.destroy(); } catch { /* ignore */ }
      return;
    }
    const start = performance.now();
    const from = this.getCurrentVolume(cur);
    const step = () => {
      const t = Math.min(1, (performance.now() - start) / FADE_MS);
      const v = from * (1 - t);
      this.applyVolume(cur, v);
      if (t < 1) {
        this.fadeTimer = requestAnimationFrame(step);
      } else {
        try { cur.stop(); cur.destroy(); } catch { /* ignore */ }
        this.fadeTimer = undefined;
      }
    };
    this.fadeTimer = requestAnimationFrame(step);
  }

  private applyVolume(sound: Phaser.Sound.BaseSound, v: number) {
    const s = sound as unknown as { setVolume?: (v: number) => void; volume?: number };
    if (typeof s.setVolume === 'function') s.setVolume(v);
    else s.volume = v;
  }

  private getCurrentVolume(sound: Phaser.Sound.BaseSound): number {
    const s = sound as unknown as { volume?: number };
    return typeof s.volume === 'number' ? s.volume : this.volume;
  }
}

export const AudioManager = new AudioManagerImpl();
